import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { MessageService } from 'primeng/api'

import { PaymentBatchStateService, StepKey } from '@modules/private/payment/data/services/view/payment-batch-state.service'
import { ApiPaymentBatchService } from '@modules/private/payment/data/services/api/api-payment-batch.service'
import { ApiWalletService } from '@modules/private/wallet/data/services/api/api-wallet.service'

/**
 * El asistente de carga de pagos: de un archivo a los pagos aplicados, en un solo viaje.
 *
 * Este componente **solo arma los pasos, controla cuál está activo y delega**. Todo lo demás vive en
 * un componente por paso y en `PaymentBatchStateService`. No es purismo: los dos multipaso que ya
 * tenía el repo son de dos y tres pasos en un solo componente, y este tiene seis con dos puntos de no
 * retorno. Un archivo con las seis pantallas dentro se vuelve imposible de revisar justo donde más
 * hace falta revisar — el paso que crea cuarenta pagos y el que reparte el dinero.
 *
 * **El paso activo se identifica por CLAVE.** PrimeNG numera solo lo renderizado y aquí los pasos son
 * tres o seis según el interruptor, así que un índice significa cosas distintas según el modo.
 */
@Component({
	selector: 'app-payment-batch-wizard',
	templateUrl: './payment-batch-wizard.component.html',
	styleUrls: ['./payment-batch-wizard.component.scss'],
	providers: [PaymentBatchStateService, MessageService],
})
export class PaymentBatchWizardComponent implements OnInit {
	/**
	 * El lote que hay que recuperar, o vacío para empezar de cero.
	 *
	 * Lo pasa Cobranza desde `?carga=<uploadBatch>`. Antes se leía del parámetro de la ruta, cuando
	 * el asistente era una pantalla completa; ahora vive en un modal encima de la pantalla que va a
	 * cambiar, que es donde tiene sentido: cargar los pagos del día es una TAREA de Cobranza, no un
	 * destino al que salir.
	 */
	@Input() uploadBatch = ''

	/** Se acabó: por «Salir» o porque el lote se aplicó. Cobranza cierra el modal y recarga sus cifras. */
	@Output() finished = new EventEmitter<void>()

	/** El lote ya existe y su huella tiene que quedar en la dirección, ya. */
	@Output() batchCreated = new EventEmitter<string>()

	/** Las etiquetas de la tira, en orden. La tira es propia: numera y marca, no pinta íconos. */
	steps: { label: string }[] = []
	/** Recuperando un lote por su ruta. Mientras tanto no se pinta ningún paso. */
	loading = false
	loadError = ''

	constructor(
		public readonly state: PaymentBatchStateService,
		private readonly api: ApiPaymentBatchService,
		private readonly apiWallet: ApiWalletService,
		private readonly messageService: MessageService
	) {}

	ngOnInit(): void {
		this.buildSteps()
		this.loadMoraSetting()

		if (this.uploadBatch) this.recover(this.uploadBatch)
	}

	/**
	 * Los pasos que ve el operador. Se rearman cuando cambia el modo, que es lo que decide si son tres
	 * o seis (D6).
	 */
	buildSteps(): void {
		this.steps = this.state.visibleSteps.map((s) => ({ label: s.label }))
	}

	get activeIndex(): number {
		return this.state.activeIndex
	}

	get activeKey(): StepKey {
		return this.state.activeKey
	}

	/**
	 * Si de verdad se puede volver a ese paso. **La regla vive aquí, una sola vez**, y la usan las dos
	 * puntas: la tira para deshabilitar el paso, y el clic para rechazarlo.
	 *
	 * Estaba solo en el clic, así que la tira ofrecía los seis pasos como si todos fueran alcanzables
	 * y la mitad no respondía. Un clic que no hace nada se lee como una pantalla rota, no como una
	 * regla de negocio.
	 */
	puedeIr(index: number): boolean {
		if (index >= this.activeIndex) return false

		const destino = this.state.visibleSteps[index]
		if (!destino) return false

		//* Registrar y aplicar no se deshacen: los pasos previos al alta dejan de ser navegables en
		//* cuanto los pagos existen. Enseñarlos como si se pudiera volver sería mentir.
		return !(this.state.registered && (destino.key === 'archivo' || destino.key === 'revision'))
	}

	/**
	 * Volver a un paso anterior desde la tira.
	 *
	 * La posición se traduce a CLAVE con la lista visible, nunca se usa como número. Y solo hacia
	 * atrás: saltar hacia adelante se saltaría una decisión.
	 */
	onStepClick(index: number): void {
		if (!this.puedeIr(index)) return

		this.state.goTo(this.state.visibleSteps[index].key)
	}

	/** Cada paso avisa cuando termina lo suyo; el padre solo mueve la aguja. */
	onStepDone(): void {
		this.buildSteps()
		this.state.next()
	}

	onBack(): void {
		this.state.back()
	}

	/**
	 * Los pagos ya existen: la dirección pasa a llevar la huella del lote, **en ese mismo instante**.
	 *
	 * Ocurre al registrar y no al avanzar de paso: si el operador cierra la pestaña delante del
	 * resumen del alta, la dirección que necesita para volver ya tiene que estar donde se copia sola.
	 *
	 * Lo escribe Cobranza en `?carga=`, no este componente. Antes era una ruta y había que cambiarla
	 * con `replaceState` para que Angular no destruyera y recreara el asistente a media carga —lo que
	 * además saludaba con un «Lote recuperado» justo después de registrar—. Como parámetro de consulta
	 * el problema desaparece: cambia la dirección sin tocar qué componente está montado.
	 */
	onBatchCreated(): void {
		this.batchCreated.emit(this.state.uploadBatch)
	}

	/** Cambiar de modo rearma la tira: son tres pasos o seis. */
	onModeChange(): void {
		this.buildSteps()
	}

	salir(): void {
		this.finished.emit()
	}

	/**
	 * Retoma un lote ya registrado.
	 *
	 * Los destinos elegidos NO se recuperan y se vuelven a decidir: entre una sesión y otra el estado
	 * de los arrendamientos pudo cambiar, y un reparto calculado sobre cifras viejas es peor que no
	 * tener ninguno.
	 */
	private recover(uploadBatch: string): void {
		//* Si el lote ya está cargado en memoria —acabamos de registrarlo y la URL cambió sola— no hay
		//* nada que recuperar.
		if (this.state.uploadBatch === uploadBatch) return

		this.loading = true
		this.api.findBatch(uploadBatch).subscribe((r) => {
			this.loading = false
			if (r.error || !r.data) {
				this.loadError = r.msg || 'No se pudo recuperar el lote.'
				return
			}

			this.state.setRecovered(uploadBatch, r.data.payments, r.data.notice)
			this.buildSteps()
			//* Se retoma en Destinos: los pagos ya existen, así que el archivo y el alta quedaron atrás.
			//* Si el lote no tuviera nada que decidir, `goTo` lo rechazaría —el paso no estaría en la
			//* lista visible— y el asistente se quedaría en «Archivo», sin archivo. Se pregunta.
			this.state.goTo(this.state.needsDestinations ? 'destinos' : 'impacto')
			this.messageService.add({ severity: 'info', summary: 'Lote recuperado', detail: `${r.data.payments.length} pago(s) de esta carga. Los destinos se vuelven a elegir.`, life: 7000 })
		})
	}

	/**
	 * Si la mora está encendida. Decide el DESGLOSE que enseña el selector, no la capacidad.
	 *
	 * Si la llamada falla se queda apagada, que es el estado de hoy: lo peor que pasa es que el
	 * desglose diga que todo es renta. Perder el reparto por no poder leer una bandera sería peor.
	 */
	private loadMoraSetting(): void {
		this.apiWallet.getSettings().subscribe((r) => {
			this.state.moraEnabled = !r.error && !!(r.data as any)?.moraEnabled
		})
	}
}
